import type { StateCreator } from 'zustand';
import type {
  UIState,
  Notification,
  OfflineState,
  ResourceInfo,
  ResourceType,
  UploadTaskStatus,
} from '@/types';

export interface UISlice {
  ui: UIState;
  offline: OfflineState;

  // UI actions
  toggleSidebar: () => void;
  setMobile: (isMobile: boolean) => void;
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  openModal: (type: string, data?: unknown) => void;
  closeModal: () => void;

  addUploadTask: (
    name: string,
    options?: {
      resourceInfo?: ResourceInfo;
      initialStatus?: UploadTaskStatus;
      controller?: AbortController;
    }
  ) => string;
  updateUploadProgress: (id: string, progress: number) => void;
  setUploadStatus: (id: string, status: UploadTaskStatus) => void;
  removeUploadTask: (id: string) => void;
  removeUploadTaskByResourceId: (
    resourceId: string,
    resourceType: ResourceType
  ) => void;
  cancelUploadTask: (id: string) => void;

  // Offline actions
  setOnlineStatus: (isOnline: boolean) => void;
  addToPendingSync: (id: string) => void;
  removeFromPendingSync: (id: string) => void;
  updateLastSync: (timestamp: string) => void;
}

export const createUISlice: StateCreator<UISlice, [], [], UISlice> = (
  set,
  get
) => ({
  ui: {
    sidebarOpen: true,
    isMobile: false,
    notifications: [],
    uploadTasks: [],
    modal: {
      open: false,
      type: null,
      data: null,
    },
  },

  offline: {
    isOnline: navigator.onLine,
    pendingSync: [],
    lastSync: null,
  },

  toggleSidebar: () => {
    set((state) => ({
      ui: {
        ...state.ui,
        sidebarOpen: !state.ui.sidebarOpen,
      },
    }));
  },

  setMobile: (isMobile: boolean) => {
    set((state) => ({
      ui: {
        ...state.ui,
        isMobile,
      },
    }));
  },

  addNotification: (notification: Omit<Notification, 'id'>) => {
    const id = Date.now().toString();
    set((state) => ({
      ui: {
        ...state.ui,
        notifications: [
          ...state.ui.notifications,
          { ...notification, id, createdAt: new Date().toISOString() },
        ],
      },
    }));
  },

  removeNotification: (id: string) => {
    set((state) => ({
      ui: {
        ...state.ui,
        notifications: state.ui.notifications.filter((n) => n.id !== id),
      },
    }));
  },

  openModal: (type: string, data?: unknown) => {
    set((state) => ({
      ui: {
        ...state.ui,
        modal: {
          open: true,
          type,
          data,
        },
      },
    }));
  },

  closeModal: () => {
    set((state) => ({
      ui: {
        ...state.ui,
        modal: {
          open: false,
          type: null,
          data: null,
        },
      },
    }));
  },

  setOnlineStatus: (isOnline: boolean) => {
    set((state) => ({
      offline: {
        ...state.offline,
        isOnline,
      },
    }));
  },

  addToPendingSync: (id: string) => {
    set((state) => ({
      offline: {
        ...state.offline,
        pendingSync: [...state.offline.pendingSync, id],
      },
    }));
  },

  removeFromPendingSync: (id: string) => {
    set((state) => ({
      offline: {
        ...state.offline,
        pendingSync: state.offline.pendingSync.filter((item) => item !== id),
      },
    }));
  },

  updateLastSync: (timestamp: string) => {
    set((state) => ({
      offline: {
        ...state.offline,
        lastSync: timestamp,
      },
    }));
  },

  addUploadTask: (
    name: string,
    options?: {
      resourceInfo?: ResourceInfo;
      initialStatus?: UploadTaskStatus;
      controller?: AbortController;
    }
  ) => {
    const id = crypto.randomUUID();
    const status = options?.initialStatus || 'uploading';

    set((state) => ({
      ui: {
        ...state.ui,
        uploadTasks: [
          ...state.ui.uploadTasks,
          {
            id,
            name,
            progress: 0,
            status,
            ...(options?.resourceInfo && {
              resourceInfo: options.resourceInfo,
            }),
            ...(options?.controller && { controller: options.controller }),
          },
        ],
      },
    }));
    return id;
  },

  updateUploadProgress: (id: string, progress: number) => {
    set((state) => ({
      ui: {
        ...state.ui,
        uploadTasks: state.ui.uploadTasks.map((task) =>
          task.id === id
            ? {
                ...task,
                progress,
                status: progress >= 100 ? 'processing' : task.status,
              }
            : task
        ),
      },
    }));
  },

  setUploadStatus: (id: string, status: UploadTaskStatus) => {
    set((state) => ({
      ui: {
        ...state.ui,
        uploadTasks: state.ui.uploadTasks.map((task) =>
          task.id === id ? { ...task, status } : task
        ),
      },
    }));
  },

  removeUploadTask: (id: string) => {
    set((state) => ({
      ui: {
        ...state.ui,
        uploadTasks: state.ui.uploadTasks.filter((task) => task.id !== id),
      },
    }));
  },

  removeUploadTaskByResourceId: (
    resourceId: string,
    resourceType: ResourceType
  ) => {
    set((state) => ({
      ui: {
        ...state.ui,
        uploadTasks: state.ui.uploadTasks.filter(
          (task) =>
            !(
              task.resourceInfo?.id === resourceId &&
              task.resourceInfo?.type === resourceType
            )
        ),
      },
    }));
  },

  cancelUploadTask: (id: string) => {
    const task = get().ui.uploadTasks.find(
      (uploadTask) => uploadTask.id === id
    );

    if (task?.controller) {
      task.controller.abort();
    }

    set((state) => ({
      ui: {
        ...state.ui,
        uploadTasks: state.ui.uploadTasks.map((uploadTask) =>
          uploadTask.id === id
            ? { ...uploadTask, status: 'cancelled', controller: undefined }
            : uploadTask
        ),
      },
    }));
  },
});
